import React, { Suspense, useCallback, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  useLocation,
} from "react-router-dom";
import type { Location } from "history";
import Header from './components/widgets/Header';
import Footer from './components/widgets/Footer';
import SiteNotice from './components/widgets/SiteNotice';
import Home from './components/pages/Home';
import Store from './components/pages/Store';
import ItemDetailsPage from './components/pages/ItemDetailsPage';
import CartContext, { CartContents } from './contexts/CartContext'
import CategoryContext, { CategoryState } from './contexts/CategoryContext'
import ConfigContext from './contexts/ConfigContext'
import { CustomerProvider } from './contexts/CustomerContext'
import { SignInDialogProvider } from './components/auth/SignInDialog'
import useStickyState from "./hooks/stickyState";
import About from "./components/pages/About";
import ExportCsv from './components/pages/ExportCsv';
import API from './API';
import CartDrawer from "./components/widgets/cart/CartDrawer";
import ModifyCartPage from "./components/pages/ModifyCartPage";
import CheckoutPage from "./components/pages/CheckoutPage";
import AgeGate from "./components/auth/AgeGate";
import InvoicePage from "./components/pages/InvoicePage";
import ContactPage from "./components/pages/ContactPage";
import NewsPage from "./components/pages/NewsPage";
import AccountPage from "./components/pages/AccountPage";
import Ungated from "./components/auth/Ungated";
import TOS from "./components/pages/legal/TOS";
import SiteDisabled from "./components/auth/SiteDisabled";
import ConfigTemplate from "./components/auth/Config";
import Style from "./components/widgets/Style";
import TenantRoute from "./components/routing/TenantRoute";
import ScrollToHash from "./components/routing/ScrollToHash";
import ErrorBoundary from "./components/ErrorBoundary";
import { HelmetProvider } from "react-helmet-async";

// Lazy load these things for speed. Users are less likely to visit these pages.
const Privacy = React.lazy(() => import("./components/pages/legal/Privacy"));
const Shipping = React.lazy(() => import("./components/pages/legal/Shipping"));
const Returns = React.lazy(() => import("./components/pages/legal/Returns"));
const Payment = React.lazy(() => import("./components/pages/legal/Payment"));
const Order = React.lazy(() => import("./components/pages/legal/Order"));
const Attributions = React.lazy(() => import("./components/pages/legal/Attributions"));

const normalizeCart = (cart: CartContents): CartContents => ({
  ...cart,
  items: cart.items ?? {},
  deliveryAddress: {
    ...cart.deliveryAddress,
    firstName: cart.deliveryAddress?.firstName ?? '',
    lastName: cart.deliveryAddress?.lastName ?? '',
    street: cart.deliveryAddress?.street ?? '',
    additional: cart.deliveryAddress?.additional ?? '',
    city: cart.deliveryAddress?.city ?? '',
    province: cart.deliveryAddress?.province ?? '',
    postal: cart.deliveryAddress?.postal ?? '',
  },
  customerAddress: {
    ...cart.customerAddress,
    firstName: cart.customerAddress?.firstName ?? '',
    lastName: cart.customerAddress?.lastName ?? '',
    email: cart.customerAddress?.email ?? '',
    phone: cart.customerAddress?.phone ?? '',
    dob: cart.customerAddress?.dob ?? {},
  },
  shippingMethod: cart.shippingMethod ?? '',
  paymentMethod: cart.paymentMethod ?? '',
});

// Page routing with the "background location" modal pattern. When a
// `Link` click sets `state.background = currentLocation`, the URL
// flips to (say) `/r/<tenant>/item/<id>` but the main Switch keeps
// rendering the page the user came from. The item-details dialog
// renders as a sibling overlay against the real URL.
//
// This is the standard React Router v5 modal-as-route trick: it keeps
// the currently-rendered page mounted (so its scroll position is
// preserved) while a deep-linkable URL still drives the dialog.
//
// Direct navigation to `/r/<tenant>/item/<id>` (no background in
// state) falls through to the existing behavior — the inner Switch
// matches the route, Store renders, and Store's own ItemDetailsPage
// shows the dialog over Shop's empty subcategory panel.
const RoutedBody = () => {
  const realLocation = useLocation();
  const background = (realLocation.state as { background?: Location } | null)?.background;
  const switchLocation = background ?? realLocation;

  return (
    <>
      <Switch location={switchLocation}>
        <Route path="/privacy"><Privacy /></Route>
        <Route path="/tos"><TOS /></Route>
        <Route path="/payment"><Payment /></Route>
        <Route path="/ordering"><Order /></Route>
        <Route path="/shipping"><Shipping /></Route>
        <Route path="/returns"><Returns /></Route>
        <Route path="/attributions"><Attributions /></Route>
        <Route path="/contact"><ContactPage /></Route>
        <Route path="/news"><NewsPage /></Route>

        <Route path="/order/cart"><ModifyCartPage /></Route>
        <Route path="/order/checkout"><CheckoutPage /></Route>
        <Route path="/order/:orderId"><InvoicePage /></Route>
        <Route path="/account"><AccountPage /></Route>
        <Route path="/about"><About /></Route>
        <Route path="/export/csv"><ExportCsv /></Route>

        <Route path="/r/:store">
          <TenantRoute>
            <Switch location={switchLocation}>
              <Route path="/r/:store/category/:group/:categoryId"><Store /></Route>
              <Route path="/r/:store/category/:categoryId"><Store /></Route>
              <Route path="/r/:store/category"><Store /></Route>
              <Route path="/r/:store/search/:searchText"><Store /></Route>
              {/* New Arrivals / Best Sellers / Hot Sellers: each
                  with a nested /item/:itemId so a card click
                  keeps the grid as the dialog background. Must
                  precede the bare /item/:itemId catch-all. */}
              <Route path="/r/:store/new-arrivals/item/:itemId"><Store /></Route>
              <Route path="/r/:store/new-arrivals"><Store /></Route>
              <Route path="/r/:store/best-sellers/item/:itemId"><Store /></Route>
              <Route path="/r/:store/best-sellers"><Store /></Route>
              <Route path="/r/:store/hot-sellers/item/:itemId"><Store /></Route>
              <Route path="/r/:store/hot-sellers"><Store /></Route>
              <Route path="/r/:store/item/:itemId"><Store /></Route>
              <Route path="/r/:store"><Store /></Route>
            </Switch>
          </TenantRoute>
        </Route>

        <Route exact path="/"><Home /></Route>
      </Switch>

      {/* Dialog overlay — only renders when a Link click supplied a
          background. Bare Switch (no `location` prop) matches against
          the real URL so the dialog reads the correct `itemId`. */}
      {background && (
        <Switch>
          <Route path="/r/:store/new-arrivals/item/:itemId"><ItemDetailsPage /></Route>
          <Route path="/r/:store/best-sellers/item/:itemId"><ItemDetailsPage /></Route>
          <Route path="/r/:store/hot-sellers/item/:itemId"><ItemDetailsPage /></Route>
          <Route path="/r/:store/item/:itemId"><ItemDetailsPage /></Route>
        </Switch>
      )}
    </>
  );
};

const App = () => {
  const [cartOpen, setCartOpen] = React.useState(false);

  const [ config, setConfig ] = React.useState<Record<string, any>>({});
  
  const [cart, setCartState] = useStickyState<CartContents>({
    items: {},
    deliveryAddress: {
      firstName: '',
      lastName: '',
      street: '',
      additional: '',
      city: '',
      province: '',
      postal: ''
    },
    cardholderAddress: {
      firstName: '',
      lastName: '',
      street: '',
      additional: '',
      city: '',
      province: '',
      postal: ''
    },
    customerAddress: {},
    distance: -1,
    shippingMethod: '',
    paymentMethod: '',
  }, 'backroom-cart');

  const [category, setCategoryState] = useState<CategoryState>({
    categories: []
  });

  useEffect(() => {
    const normalized = normalizeCart(cart);
    if (JSON.stringify(normalized) !== JSON.stringify(cart)) {
      setCartState(normalized);
    }
    // Only run once on mount to migrate any partial cart shape from localStorage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stable refs — TenantRoute lists doCategoryRefresh in its useEffect deps,
  // so an unstable function reference would re-trigger the effect on every
  // App re-render and cancel the in-flight load before it can finish.
  const fetchCategories = useCallback(async () => {
    try {
      const roots = await API.getCategories();
      setCategoryState({ categories: roots });
    }
    catch (err: any) {
      console.error(err.message);
    }
  }, []);

  /**
   * Load categories on startup
   */
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  /**
   * Request refresh. Returns the fetch promise so callers (e.g. TenantRoute)
   * can await it before unblocking render.
   */
  const doCategoryRefresh = useCallback(() => fetchCategories(), [fetchCategories]);

  return (
    <HelmetProvider>
      <ConfigContext.Provider value={{ config, setConfig }}>
        <Style></Style>

        <Router>
          <ScrollToHash />
          <ConfigTemplate>
            <CustomerProvider>
              <SignInDialogProvider>
              <CartContext.Provider value={{ cart, setCartState }}>
                <CategoryContext.Provider value={{ category, setCategoryState, doCategoryRefresh }}>
                  <main className='App'>
                    <SiteNotice />
                    <Suspense fallback={<div>Loading...</div>}>

                      <Header setCartOpen={setCartOpen} />
                      <div className='app-body'>
                        <ErrorBoundary>
                          <CartDrawer cartOpen={cartOpen} setCartOpen={setCartOpen}/>
                          {/* A <Switch> looks through its children <Route>s and
                            renders the first one that matches the current URL. */}
                          <Ungated>
                            <Switch>
                              <Route exact path="/privacy"><Privacy /></Route>
                              <Route exact path="/tos"><TOS /></Route>
                              <Route exact path="/payment"><Payment /></Route>
                              <Route exact path="/ordering"><Order /></Route>
                              <Route exact path="/shipping"><Shipping /></Route>
                              <Route exact path="/returns"><Returns /></Route>
                              <Route exact path="/attributions"><Attributions /></Route>
                              <Route exact path="/contact"><ContactPage /></Route>
                            </Switch>
                          </Ungated>
        
                          <AgeGate>
                            <SiteDisabled>
                              <RoutedBody />
                            </SiteDisabled>
                          </AgeGate>
                        </ErrorBoundary>
                      </div>
                      <Footer />
                    
                    </Suspense>
                  </main>
                </CategoryContext.Provider>
              </CartContext.Provider>
              </SignInDialogProvider>
            </CustomerProvider>
          </ConfigTemplate>
        </Router>
      </ConfigContext.Provider>
    </HelmetProvider>
  )
}

export default App